import { supabaseCoreClient } from "./supabase";
import { deleteRecord, callRpc, handleOperationError } from './supabase-operations';
import { getFieldDependentTable } from './metadata/index';
import type { BaseRecord } from '../types/api';
import type { EnrichedTableField } from '../types/metadata';
import { normalizePhone } from './searchNormalization';
import { buildTableSearchConditions } from './tableSearchConditions';

export { isValidEmail, isValidPhone, normalizeEmail, normalizePhone } from './searchNormalization';
export { buildTableSearchConditions } from './tableSearchConditions';

// Search history record structure
export interface SearchHistoryRecord extends BaseRecord {
  search_query: string;
  search_query_normalized: string | null;
}

// Parameters for executeTableSearch function
export interface ExecuteTableSearchParams {
  searchTerm: string;
  tableName: string;
  fields: EnrichedTableField[];
  schema: string;
}

// TODO use metadata system instead of direct Supabase access.
export async function getRecentSearches(prefix: string, limit: number): Promise<SearchHistoryRecord[]> {
    const literalPrefix = prefix.replace(/[\\%_]/g, '\\$&');
    const { data, error } = await supabaseCoreClient
      .from('search_history')
      .select('*')
      .ilike('search_query', `${literalPrefix}%`)
      .order('updated_at', { ascending: false })
      .limit(limit);
      
    if (error) {
      throw error;
    }
    return data as unknown as SearchHistoryRecord[];
    
  }
  
export async function deleteSearchEntry(id: number): Promise<void> {
    try {
        await deleteRecord({
            tableName: 'search_history',
            schema: 'core_crm',
            whereClause: { id },
            options: {
                invalidateCache: false, // Search history doesn't need cache invalidation
                returnData: false
            }
        });
    } catch (error) {
        const errorInfo = handleOperationError(error, 'Error deleting search entry');
        throw new Error(errorInfo.userMessage);
    }
}

export async function insertSearchEntry(searchTerm: string): Promise<SearchHistoryRecord | undefined> {
    if(!searchTerm){
        return;
    }
    const { data } = await callRpc({
        schema: 'core_crm',
        functionName: 'insert_search_history',
        params: { search_query: searchTerm }
    });
    return data as SearchHistoryRecord;
}

/**
 * Trims whitespace from email without changing casing.
 * Returns trimmed email, or null if empty.
 * Note: This does NOT lowercase the email (unlike normalizeEmail).
 */
export function trimEmail(email: string | null | undefined): string | null {
  if (!email || typeof email !== 'string') {
    return null;
  }
  const trimmed = email.trim();
  return trimmed === '' ? null : trimmed;
}


/**
 * Validates and normalizes phone for saving.
 * Returns { valid: true, normalized: string } or { valid: false, error: string }
 */
export function validateAndNormalizePhone(phone: string | null | undefined):
  { valid: true; normalized: string } | { valid: false; error: string } {
  if (!phone || typeof phone !== 'string' || phone.trim() === '') {
    return { valid: false, error: 'Phone number is required' };
  }

  try {
    const normalized = normalizePhone(phone);
    if (normalized === null) {
      return { valid: false, error: 'Phone number is required' };
    }
    return { valid: true, normalized };
  } catch (error) {
    return { valid: false, error: (error as Error).message };
  }
}

  
  /**
   * Async function to execute a table search using Supabase.
   *
   * Algorithm:
   * - Ensure that there is at least one searchable column.
   * - If the search term is a valid email and there are searchable email columns:
   *     - Normalize it.
   *     - Build an OR clause for each searchable email column (using ui_data.search_field) with an exact match.
   * - Else if the search term is a valid phone and there are searchable phone columns:
   *     - Normalize it.
   *     - Build an OR clause for each searchable phone column with an exact match.
   * - Otherwise, perform a full text search on the "full_text_search" column.
   */
  export async function executeTableSearch({ searchTerm, tableName, fields, schema }: ExecuteTableSearchParams): Promise<BaseRecord[]> {
    const searchConditions = buildTableSearchConditions({ searchTerm, fields });
    if (Object.keys(searchConditions).length === 0) return [];

    const { data, error } = await getFieldDependentTable(
        {
            tableName,
            schema,
            conditions: searchConditions,
            orderBy: { column: 'updated_at', direction: 'descending' },
            limitOffset: { limit: 10, offset: 0 },
            withCount: false,
            noCache: true,
            // REG-669: deliberately not '*'. Every expanded relationship adds a
            // lateral over the target's calculated-fields view, and selecting
            // every column forces all of them to evaluate — 410ms versus 2ms on
            // submission_view. Falling through to the table's view_columns picks
            // up the same fields minus the expanded ones, which search never
            // shows anyway.
        }
    )

    if (error) {
      throw error;
    }
    
    // Ensure we return an array of BaseRecord
    if (!Array.isArray(data)) {
        return [];
    }
    return data;
  }
  
// Make executeTableSearch available globally (for debugging purposes)
declare global {
  interface Window {
    executeTableSearch?: typeof executeTableSearch;
  }
}

window.executeTableSearch = executeTableSearch;
